import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, Package, CreditCard, CheckCircle, MapPin, Store } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import OrderItemDetails from '../components/OrderItemDetails';

import { readPendingCheckout, readCheckoutOrder, forgetPendingCheckout, savePendingCheckout, checkoutError, newCheckoutAttemptId, isDefiniteCheckoutRejection, reconcilePaidCheckout } from '../utils/checkoutRecovery';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const { cart: liveCart, sessionId, refreshCart } = useCart();
  const [pending, setPending] = useState(() => {
    const returned = readCheckoutOrder(new URLSearchParams(window.location.search).get('order_id'));
    return returned?.sessionId === sessionId ? returned : readPendingCheckout(sessionId);
  });
  const inFlight = useRef(false);
  const cart = pending?.cart || liveCart;
  const { user, token } = useAuth();
  const { customer, token: customerToken } = useCustomerAuth();

  const activeUser = customer || user;
  const activeToken = customerToken || token;

  const [step, setStep] = useState(pending ? 2 : 1); // 1: shipping, 2: review, 3: confirmed
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(pending?.order || null);
  const [error, setError] = useState('');
  const [previousOrder, setPreviousOrder] = useState(null);
  // Saved orders remain immutable, but must not trap a newly edited cart.
  const cartChanged = !!pending?.order && liveCart.items.length > 0 && (
    liveCart.items.length !== pending.cart.items.length || liveCart.items.some(item =>
      !pending.cart.items.some(saved => saved.id === item.id &&
        saved.product_id === item.product_id && (saved.variant_id || null) === (item.variant_id || null) &&
        saved.quantity === item.quantity && (saved.customization_text || null) === (item.customization_text || null) &&
        JSON.stringify(saved.customization_options || null) === JSON.stringify(item.customization_options || null))));

  const checkoutCurrentCart = () => {
    if (inFlight.current || !cartChanged) return;
    // Keep the per-order recovery record. Only release the active cart pointer.
    forgetPendingCheckout(sessionId, orderResult.id);
    setPreviousOrder(orderResult);
    setPending(null);
    setOrderResult(null);
    setError('');
    setStep(1);
    const url = new URL(window.location.href);
    url.searchParams.delete('order_id');
    url.searchParams.delete('cancelled');
    window.history.replaceState({}, '', url.pathname + url.search);
  };

  const [form, setForm] = useState(pending?.form || {
    email: activeUser?.email || '',
    phone: '',
    name: activeUser ? `${activeUser.first_name || ''} ${activeUser.last_name || ''}`.trim() : '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    notes: '',
    is_rush: false,
    fulfillment_method: 'shipping',  // in_store_pickup | shipping
  });

  const tax = cart.subtotal * 0.06;
  const fm = form.fulfillment_method;
  const needsAddress = fm === 'shipping';
  const shipping = (() => {
    if (fm === 'in_store_pickup') return 0;
    return cart.subtotal > 75 ? 0 : 9.99;
  })();
  const shippingLabel = {
    in_store_pickup: 'In-store pickup',
    shipping: 'Shipping',
    // Labels retained only for previously saved orders, not selectable options.
    delivery: 'Local delivery',
    pop_up_pickup: 'Pop-up pickup',
  }[fm] || 'Shipping';
  const rushFee = form.is_rush ? 25 : 0;
  const total = cart.subtotal + tax + (cart.subtotal > 0 ? shipping : 0) + rushFee;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleShippingSubmit = (e) => {
    e.preventDefault();
    setStep(2);
  };

  const handlePlaceOrder = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError('');
    // If the order was already created but payment failed to start,
    // reuse it — never create a duplicate order on retry.
    let order = orderResult;
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (activeToken) headers['Authorization'] = `Bearer ${activeToken}`;

      if (order) {
        const resumeHeaders = { ...headers };
        if (order.checkout_token) resumeHeaders['X-Checkout-Token'] = order.checkout_token;
        let resume;
        try {
          resume = await axios.get(`${API_URL}/api/orders/${order.id}/checkout`, { headers: resumeHeaders });
        } catch (resumeError) {
          if (resumeError.response?.status !== 404) throw resumeError;
          const recoveryMessage = 'We could not restore access to your saved order. Please contact the shop with your order number before trying a new checkout. Your cart has been kept.';
          if (!pending?.id || !pending?.payload) throw { response: { data: { detail: recoveryMessage } } };
          let recovered;
          try {
            recovered = await axios.post(`${API_URL}/api/orders/${order.id}/checkout/recover`,
              { ...pending.payload, checkout_attempt_id: pending.id }, { headers });
          } catch (recoveryError) {
            if ([404, 422].includes(recoveryError.response?.status)) {
              throw { response: { data: { detail: recoveryMessage } } };
            }
            throw recoveryError;
          }
          order = { ...order, checkout_token: recovered.data.checkout_token };
          const saved = { ...pending, order };
          savePendingCheckout(sessionId, saved);
          setPending(saved);
          setOrderResult(order);
          resumeHeaders['X-Checkout-Token'] = order.checkout_token;
          resume = await axios.get(`${API_URL}/api/orders/${order.id}/checkout`, { headers: resumeHeaders });
        }
        if (resume.data.payment_status === 'Paid in Full') {
          setOrderResult({ ...order, ...resume.data });
          try {
            await reconcilePaidCheckout(pending);
            await refreshCart?.();
            forgetPendingCheckout(sessionId, order.id);
          } catch {
            // Payment is confirmed; keep recovery state for a later cleanup retry.
          }
          setStep(3);
          setSubmitting(false);
          return;
        }
      }

      if (!order) {
        const orderPayload = {
        items: cart.items.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id || null,
          quantity: item.quantity,
          customization_text: item.customization_text || null,
          customization_options: item.customization_options || null,
        })),
        shipping_address: needsAddress ? {
          name: form.name,
          street_address: form.street_address,
          city: form.city,
          state: form.state,
          zip_code: form.zip_code,
          country: form.country,
          phone: form.phone,
        } : {
          name: form.name,
          phone: form.phone,
          country: form.country,
        },
        billing_address: needsAddress ? {
          name: form.name,
          street_address: form.street_address,
          city: form.city,
          state: form.state,
          zip_code: form.zip_code,
          country: form.country,
          phone: form.phone,
        } : {
          name: form.name,
          phone: form.phone,
          country: form.country,
        },
          guest_email: form.email.trim(),
          guest_phone: form.phone || null,
          notes: form.notes || null,
          is_rush: form.is_rush,
          fulfillment_method: fm,
        };

        const attempt = pending || { id: newCheckoutAttemptId(), form, cart, sessionId, payload: orderPayload };
        savePendingCheckout(sessionId, attempt);
        setPending(attempt);
        const res = await axios.post(`${API_URL}/api/orders`, { ...attempt.payload, checkout_attempt_id: attempt.id }, { headers });
        order = res.data;
        setOrderResult(order);
        const saved = { ...attempt, order };
        savePendingCheckout(sessionId, saved);
        setPending(saved);
      }

      // Start Stripe checkout for the order and hand the customer to payment.
      const checkoutRes = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { order_id: order.id, origin_url: window.location.origin, checkout_token: order.checkout_token },
        { headers }
      );
      if (!checkoutRes.data.url) throw new Error('Payment URL missing');
      window.location.href = checkoutRes.data.url;
    } catch (err) {
      // Validation rejects the request before order creation. Only definitive
      // validation failures may release the immutable retry payload for editing.
      if (!order && [400, 422].includes(err.response?.status)) {
        forgetPendingCheckout(sessionId);
        setPending(null);
      }
      setError(checkoutError(err, order
        ? 'Your order is saved, but we could not start payment. Retry payment below; your cart has been kept.'
        : 'We could not create your order. Please retry; your cart has been kept.'));
      setSubmitting(false);
    } finally {
      inFlight.current = false;
    }
  };

  if (cart.items.length === 0 && !pending && step !== 3) {
    return (
      <div className="min-h-screen bg-white" data-testid="checkout-empty">
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4 text-gray-900">Your cart is empty</h1>
          <Link to="/back-to-school" className="inline-flex items-center px-8 py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  // Only a server-confirmed paid order reaches this state.
  if (step === 3 && orderResult) {
    return (
      <div className="min-h-screen bg-white" data-testid="checkout-confirmed">
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4">Payment Confirmed</h1>
          <p className="mb-6">Order #{orderResult.order_number} is already paid. No additional payment is needed.</p>
          <Link to="/back-to-school" className="text-[#4A90A4] font-bold">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="checkout-page">
      {/* Header */}
      <div className="bg-[#1B3A5C] py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/cart" className="flex items-center text-white/70 hover:text-white transition-colors text-sm">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Cart
          </Link>
          <h1 className="text-xl font-bold text-white">Checkout</h1>
          <div className="flex items-center text-white/70 text-sm">
            <Lock className="w-4 h-4 mr-1" /> Secure
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-center gap-4 text-sm mb-8">
          {[
            { num: 1, label: 'Shipping' },
            { num: 2, label: 'Review & Pay' },
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              {i > 0 && <div className={`w-16 h-0.5 ${step >= s.num ? 'bg-[#4A90A4]' : 'bg-gray-300'}`} />}
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  step >= s.num ? 'bg-[#4A90A4] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s.num ? <CheckCircle className="w-4 h-4" /> : s.num}
                </div>
                <span className={step >= s.num ? 'text-gray-900 font-medium' : 'text-gray-400'}>{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 min-w-0">
            {step === 1 && (
              <form onSubmit={handleShippingSubmit} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="shipping-form">
                {previousOrder && <p role="status" className="mb-4 text-sm text-gray-600">You are checking out your current cart. Previous order #{previousOrder.order_number} remains saved. <a className="text-[#4A90A4] underline" href={`/checkout?order_id=${encodeURIComponent(previousOrder.id)}`}>Return to previous order</a></p>}
                <h2 className="text-xl font-bold text-gray-900 mb-2">How would you like to receive your order?</h2>
                <p className="text-sm text-gray-500 mb-4">Pick a fulfillment option — we&apos;ll only ask for the details we need.</p>

                {/* Fulfillment picker */}
                <div className="grid sm:grid-cols-2 gap-2.5 mb-6" data-testid="fulfillment-picker">
                  {[
                    { key: 'in_store_pickup', label: 'In-Store Pickup', icon: Store,   hint: 'Free · Pick up at 129 Slade Ave' },
                    { key: 'shipping',        label: 'Shipping',        icon: Package, hint: 'USPS · Free over $75, otherwise $9.99' },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const active = fm === opt.key;
                    return (
                      <button key={opt.key} type="button"
                        onClick={() => setForm(p => ({ ...p, fulfillment_method: opt.key }))}
                        className={`flex items-start gap-3 text-left px-4 py-3 rounded-xl border-2 transition-all ${active ? 'border-[#F5A623] bg-[#F5A623]/5 ring-2 ring-[#F5A623]/15' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                        data-testid={`fulfillment-${opt.key}`}
                      >
                        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${active ? 'text-[#F5A623]' : 'text-gray-400'}`} />
                        <div>
                          <div className="font-bold text-sm text-[#1B3A5C]">{opt.label}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{opt.hint}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <h3 className="text-sm font-bold uppercase tracking-wider text-[#4A90A4] mb-3">
                  {needsAddress ? 'Where should we send it?' : 'Contact details'}
                </h3>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4]"
                      placeholder="your@email.com" data-testid="checkout-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4]"
                      placeholder="John Doe" data-testid="checkout-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input type="tel" name="phone" value={form.phone} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4]"
                      placeholder="(555) 123-4567" data-testid="checkout-phone" />
                  </div>
                  {needsAddress && (
                    <>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                        <input type="text" name="street_address" value={form.street_address} onChange={handleChange} required={needsAddress}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4]"
                          placeholder="123 Main St" data-testid="checkout-address" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input type="text" name="city" value={form.city} onChange={handleChange} required={needsAddress}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4]"
                          placeholder="Baltimore" data-testid="checkout-city" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                          <input type="text" name="state" value={form.state} onChange={handleChange} required={needsAddress}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4]"
                            placeholder="MD" data-testid="checkout-state" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                          <input type="text" name="zip_code" value={form.zip_code} onChange={handleChange} required={needsAddress}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4]"
                            placeholder="21207" data-testid="checkout-zip" />
                        </div>
                      </div>
                    </>
                  )}
                  {fm === 'in_store_pickup' && (
                    <div className="sm:col-span-2 p-4 bg-[#F0F7F9] rounded-xl border border-[#4A90A4]/20 flex gap-3">
                      <MapPin className="w-5 h-5 text-[#4A90A4] flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-gray-700">
                        <div className="font-semibold text-[#1B3A5C] mb-0.5">Pick up at our shop</div>
                        129 Slade Ave, Pikesville, MD 21208<br />
                        Mon–Fri 10am–6pm · <a href="tel:443-940-6660" className="text-[#4A90A4] font-semibold">443.940.6660</a>
                      </div>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Order Notes (optional)</label>
                    <textarea name="notes" value={form.notes} onChange={handleChange} rows={2}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] resize-none"
                      placeholder="Special instructions..." data-testid="checkout-notes" />
                  </div>
                </div>

                {/* Rush option */}
                <label className="flex items-center gap-3 p-4 bg-[#FDF6E9] rounded-xl border border-[#F5A623]/15 cursor-pointer mb-6">
                  <input type="checkbox" name="is_rush" checked={form.is_rush} onChange={handleChange}
                    className="w-5 h-5 rounded text-[#F5A623] focus:ring-[#F5A623]" data-testid="checkout-rush" />
                  <div>
                    <span className="font-semibold text-gray-900">Rush Order (+$25.00)</span>
                    <p className="text-sm text-gray-500">Get your order in 2-3 business days instead of 5-7</p>
                  </div>
                </label>

                <button type="submit"
                  className="w-full py-4 bg-[#4A90A4] text-white font-bold rounded-full hover:bg-[#3d7a8e] transition-colors text-lg"
                  data-testid="continue-to-review-btn">
                  Continue to Review
                </button>
              </form>
            )}

            {step === 2 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="review-step">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Review Your Order</h2>
                {cartChanged && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="checkout-cart-changed">
                    <h3 className="font-bold text-gray-900">Your cart has changed</h3>
                    <p className="mt-2 text-sm text-gray-700">This saved order contains different items from your current cart. Choose the current cart to start a separate order, or resume the saved order below.</p>
                    <ul className="my-3 text-sm text-gray-700">{liveCart.items.map(item => <li key={item.id}>{item.product_name} x{item.quantity}</li>)}</ul>
                    <button type="button" onClick={checkoutCurrentCart} disabled={submitting}
                      className="w-full py-3 bg-[#4A90A4] text-white font-bold rounded-full disabled:opacity-50"
                      data-testid="checkout-current-cart-btn">Checkout Current Cart</button>
                    <p className="mt-2 text-xs text-gray-600">Your previous order stays saved. This does not cancel or pay it.</p>
                  </div>
                )}
                {pending && <p role="status" className="mb-4 text-sm text-gray-600">{orderResult ? `Order #${orderResult.order_number || orderResult.id} is saved.` : 'Your checkout is saved.'} Resume payment below. Your cart has not been cleared; any later cart changes are not included in this order.</p>}
                {new URLSearchParams(window.location.search).get('cancelled') === '1' && <p role="status" className="mb-4 text-sm text-gray-600">Payment was cancelled. You can retry this saved order.</p>}

                {/* Shipping summary */}
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-semibold text-gray-900">{fm === 'in_store_pickup' ? 'In-store pickup' : 'Ship To'}</h3>
                    <button disabled={!!pending || submitting} onClick={() => setStep(1)} className="text-[#4A90A4] text-sm hover:underline" data-testid="edit-shipping-btn">Edit</button>
                  </div>
                  <p className="text-sm text-gray-600">{form.name}</p>
                  <p className="text-sm text-gray-600">{form.street_address}</p>
                  {fm !== 'in_store_pickup' && <p className="text-sm text-gray-600">{form.city}, {form.state} {form.zip_code}</p>}
                  <p className="text-sm text-gray-600">{form.email}</p>
                </div>

                {/* Items */}
                <div className="space-y-3 mb-6">
                  {cart.items.map(item => (
                    <div key={item.id} className="flex flex-col sm:flex-row items-start gap-4 p-3 border border-gray-100 rounded-xl">
                      <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-gray-50">
                        {item.product_image ? (
                          <img src={item.product_image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300 font-bold">
                            {item.product_name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 w-full">
                        <p className="font-medium text-gray-900">{item.product_name}</p>
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                        {(item.customization_options?.decoration || item.customization_options?.pricing?.name_per_item) && <OrderItemDetails item={item} />}
                      </div>
                      <p className="font-semibold text-gray-900">${item.total_price.toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="checkout-error">
                    {error}
                  </div>
                )}

                <div className="p-4 bg-[#FDF6E9] rounded-xl border border-[#F5A623]/15 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-5 h-5 text-[#F5A623]" />
                    <span className="font-semibold text-gray-900">Payment</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    You&apos;ll be taken to our secure Stripe checkout to pay by card. Your order goes into production as soon as payment is complete.
                  </p>
                </div>

                <button
                  onClick={handlePlaceOrder}
                  disabled={submitting}
                  className="w-full py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full hover:shadow-lg hover:shadow-[#F5A623]/25 transition-all text-lg disabled:opacity-50"
                  data-testid="place-order-btn"
                >
                  {submitting ? 'Starting Secure Payment...' : `${orderResult ? "Resume Payment" : "Place Order & Pay"} — $${(orderResult?.total ?? total).toFixed(2)}`}
                </button>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1 min-w-0">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24">
              <h3 className="font-bold text-gray-900 mb-4">Order Summary</h3>
              <div className="space-y-2 mb-4 text-sm">
                {cart.items.map(item => (
                  <div key={item.id} className="flex justify-between">
                    <span className="text-gray-500 min-w-0 truncate pr-2">{item.product_name} x{item.quantity}</span>
                    <span className="font-medium whitespace-nowrap">${item.total_price.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-100 pt-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-medium">${cart.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Tax (6%)</span><span className="font-medium">${tax.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">{shippingLabel}</span><span className="font-medium">{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span></div>
                {form.is_rush && (
                  <div className="flex justify-between"><span className="text-gray-500">Rush Fee</span><span className="font-medium text-[#F5A623]">$25.00</span></div>
                )}
              </div>
              <div className="border-t border-gray-100 pt-3 mt-3">
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-gray-900">Total</span>
                  <span className="text-[#F5A623]">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
